import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, Table, Button, Space, Tag, Modal, Input, message, 
  Checkbox, Row, Col, Tooltip, Typography, Spin, Upload, Select
} from 'antd';
import { 
  PlusOutlined, DeleteOutlined, ReloadOutlined, CopyOutlined,
  DownloadOutlined, UploadOutlined, EyeOutlined, EyeInvisibleOutlined,
  FileExcelOutlined, ImportOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// 接口定义
interface TokenBalance {
  contractAddress: string;
  symbol: string;
  decimals: number;
  balance: string;
}

interface CustomToken {
  contractAddress: string;
  symbol: string;
  decimals: number;
}

interface ChainData {
  nativeBalance: string;
  txCount: number;
  tokens: TokenBalance[];
}

interface Wallet {
  id: number;
  address: string;
  ensName?: string;
  chainData: Record<string, ChainData>;
}

interface SupportedChain {
  id: string;
  name: string;
  symbol: string;
  rpcUrl: string;
  explorerUrl: string;
  color: string;
}

// 预设支持的网络
const SUPPORTED_CHAINS: SupportedChain[] = [
  {
    id: '1',
    name: 'Ethereum',
    symbol: 'ETH',
    rpcUrl: 'https://1rpc.io/eth',
    explorerUrl: 'https://etherscan.io',
    color: '#627EEA'
  },
  {
    id: '56',
    name: 'BNB Chain',
    symbol: 'BNB',
    rpcUrl: 'https://bsc-dataseed.binance.org',
    explorerUrl: 'https://bscscan.com',
    color: '#F3BA2F'
  },
  {
    id: '137',
    name: 'Polygon',
    symbol: 'POL',
    rpcUrl: 'https://polygon-rpc.com',
    explorerUrl: 'https://polygonscan.com',
    color: '#8247E5'
  },
  {
    id: '42161',
    name: 'Arbitrum',
    symbol: 'ETH',
    rpcUrl: 'https://arb.mytokenpocket.vip',
    explorerUrl: 'https://arbiscan.io',
    color: '#28A0F0'
  },
  {
    id: '10',
    name: 'Optimism',
    symbol: 'ETH',
    rpcUrl: 'https://mainnet.optimism.io',
    explorerUrl: 'https://optimistic.etherscan.io',
    color: '#FF0420'
  },
  {
    id: '8453',
    name: 'Base',
    symbol: 'ETH',
    rpcUrl: 'https://base.publicnode.com',
    explorerUrl: 'https://basescan.org',
    color: '#0052FF'
  }
];

const WalletManager: React.FC = () => {
  // 安全的JSON序列化函数，处理BigInt类型
  const safeStringify = (obj: any): string => {
    return JSON.stringify(obj, (key, value) => {
      // 将BigInt转换为字符串并记录日志
      if (typeof value === 'bigint') {
        console.warn(`发现BigInt值在键 "${key}":`, value.toString());
        return value.toString();
      }
      return value;
    });
  };

  // 状态管理
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [selectedChainIds, setSelectedChainIds] = useState<Set<string>>(new Set(['1', '56']));
  const [displayedTokens, setDisplayedTokens] = useState<Record<string, CustomToken[]>>({});
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  const [selectedWalletIds, setSelectedWalletIds] = useState<Set<number>>(new Set());
  
  // Modal 状态
  const [isAddWalletVisible, setIsAddWalletVisible] = useState(false);
  const [isBatchImportVisible, setIsBatchImportVisible] = useState(false);
  const [isAddTokenVisible, setIsAddTokenVisible] = useState(false);
  const [isJsonImportVisible, setIsJsonImportVisible] = useState(false);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [batchAddresses, setBatchAddresses] = useState('');
  const [newTokenAddress, setNewTokenAddress] = useState('');
  const [currentChainId, setCurrentChainId] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<'json' | 'excel'>('json');
  const [importFileList, setImportFileList] = useState<any[]>([]);

  // UI 控制状态
  const [showRowTotal, setShowRowTotal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  
  // 刷新锁，防止重复刷新同一个钱包的同一个网络
  const refreshLocks = useRef<Record<string, boolean>>({});
  
  // 批量刷新锁，防止重复批量刷新
  const batchRefreshLock = useRef<boolean>(false);
  const selectedRefreshLock = useRef<boolean>(false);

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 从localStorage加载数据
  const loadDataFromStorage = useCallback(() => {
    try {
      const storedWallets = localStorage.getItem('wallet_manager_wallets');
      const storedChains = localStorage.getItem('wallet_manager_selected_chains');
      const storedDisplayedTokens = localStorage.getItem('wallet_manager_displayed_tokens');
      
      if (storedWallets) {
        setWallets(JSON.parse(storedWallets));
      }
      
      if (storedChains) {
        setSelectedChainIds(new Set(JSON.parse(storedChains)));
      }
      
      if (storedDisplayedTokens) {
        setDisplayedTokens(JSON.parse(storedDisplayedTokens));
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    }
  }, []);

  // 防抖函数 - 移到前面避免依赖问题
  const debounce = useCallback(<T extends (...args: any[]) => any>(func: T, wait: number): T => {
    let timeout: NodeJS.Timeout;
    return ((...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    }) as T;
  }, []);

  // 保存数据到localStorage的防抖函数 - 修复依赖问题
  const debouncedSave = useMemo(() => {
    return debounce(() => {
      try {
        localStorage.setItem('wallet_manager_wallets', safeStringify(wallets));
        localStorage.setItem('wallet_manager_selected_chains', safeStringify(Array.from(selectedChainIds)));
        localStorage.setItem('wallet_manager_displayed_tokens', safeStringify(displayedTokens));
        console.log('数据已保存到localStorage');
      } catch (error) {
        console.error('保存数据失败:', error);
      }
    }, 1000);
  }, [debounce, safeStringify]); // 添加safeStringify依赖

  // 独立的保存函数
  const saveDataToStorage = useCallback(() => {
    try {
      localStorage.setItem('wallet_manager_wallets', safeStringify(wallets));
      localStorage.setItem('wallet_manager_selected_chains', safeStringify(Array.from(selectedChainIds)));
      localStorage.setItem('wallet_manager_displayed_tokens', safeStringify(displayedTokens));
      console.log('数据已保存到localStorage');
    } catch (error) {
      console.error('保存数据失败:', error);
    }
  }, [wallets, selectedChainIds, displayedTokens, safeStringify]);

  // 验证以太坊地址
  const isValidAddress = (address: string): boolean => {
    return ethers.isAddress(address);
  };

  // 获取链信息
  const getChainInfo = (chainId: string): SupportedChain | undefined => {
    return SUPPORTED_CHAINS.find(chain => chain.id === chainId);
  };

  // 添加钱包
  const handleAddWallet = () => {
    const address = newWalletAddress.trim();
    if (!address) {
      message.error('请输入钱包地址');
      return;
    }

    if (!isValidAddress(address)) {
      message.error('无效的钱包地址');
      return;
    }

    // 检查是否已存在
    const existingWallet = wallets.find(w => w.address.toLowerCase() === address.toLowerCase());
    if (existingWallet) {
      message.warning('该钱包地址已存在');
      return;
    }

    const newWallet: Wallet = {
      id: Date.now(), // 使用时间戳作为ID
      address: address,
      chainData: {}
    };

    // 为每个支持的链初始化空数据
    SUPPORTED_CHAINS.forEach(chain => {
      newWallet.chainData[chain.id] = {
        nativeBalance: '0',
        txCount: 0,
        tokens: []
      };
    });

    setWallets(prev => [...prev, newWallet]);
    setNewWalletAddress('');
    setIsAddWalletVisible(false);
    message.success('钱包地址添加成功');
  };

  // 计算可用宽度
  const availableWidth = window.innerWidth <= 768 
    ? window.innerWidth - 32 // 移动端减去小边距
    : Math.min(window.innerWidth * 0.94, 1800); // 桌面端使用视窗宽度的94%，最大1800px

  // 生成动态表格列
  const generateTableColumns = () => {
    // 计算总列数：每个网络的基础列(2) + 显示的代币列数
    let totalDynamicColumns = 0;
    Array.from(selectedChainIds).forEach(chainId => {
      const chainTokenCount = displayedTokens[chainId]?.length || 0;
      totalDynamicColumns += 2 + chainTokenCount; // 主币+TX+代币列
    });

    // 智能宽度分配策略
    const selectedNetworkCount = selectedChainIds.size;
    
    // 智能计算地址列宽度 - 充分利用可用空间
    let addressColumnWidth;
    if (selectedNetworkCount <= 2) {
      // 网络少时，给地址列更多空间，确保完整显示
      addressColumnWidth = isExpanded ? Math.min(400, Math.floor(availableWidth * 0.3)) : Math.max(180, Math.floor(availableWidth * 0.15));
    } else if (selectedNetworkCount <= 4) {
      // 网络中等时，平衡地址列和网络列
      addressColumnWidth = isExpanded ? Math.min(320, Math.floor(availableWidth * 0.25)) : Math.max(160, Math.floor(availableWidth * 0.12));
    } else {
      // 网络较多时，地址列紧凑但仍可读
      addressColumnWidth = isExpanded ? Math.min(280, Math.floor(availableWidth * 0.2)) : Math.max(140, Math.floor(availableWidth * 0.1));
    }

    const fixedColumnsWidth = 50 + addressColumnWidth; // 序号 + 地址列宽度
    const remainingWidth = availableWidth - fixedColumnsWidth;

    // 根据网络数量智能分配动态列宽度
    let balanceWidth, txWidth, tokenWidth;
    
    if (selectedNetworkCount <= 2) {
      // 网络少时，给每列更多空间
      const perNetworkWidth = Math.floor(remainingWidth / selectedNetworkCount);
      balanceWidth = Math.max(100, Math.min(perNetworkWidth * 0.5, 130));
      txWidth = Math.max(60, Math.min(perNetworkWidth * 0.25, 80));
      tokenWidth = Math.max(90, Math.min(perNetworkWidth * 0.4, 120));
    } else if (selectedNetworkCount <= 4) {
      // 中等网络数量
      const perNetworkWidth = Math.floor(remainingWidth / selectedNetworkCount);
      balanceWidth = Math.max(85, Math.min(perNetworkWidth * 0.5, 110));
      txWidth = Math.max(50, Math.min(perNetworkWidth * 0.25, 70));
      tokenWidth = Math.max(75, Math.min(perNetworkWidth * 0.4, 100));
    } else {
      // 网络较多时，使用紧凑布局
      const dynamicColumnWidth = Math.max(70, Math.floor(remainingWidth / Math.max(totalDynamicColumns, 8)));
      balanceWidth = Math.max(80, Math.min(dynamicColumnWidth, 100));
      txWidth = Math.max(45, Math.floor(dynamicColumnWidth * 0.7));
      tokenWidth = Math.max(70, Math.floor(dynamicColumnWidth * 0.9));
    }

    const columns: any[] = [
      {
        title: '#',
        dataIndex: 'id',
        key: 'index',
        width: 45,
        align: 'center',
        render: (_: any, __: any, index: number) => index + 1,
        fixed: 'left'
      },
      {
        title: 'Address',
        dataIndex: 'address',
        key: 'address',
        width: addressColumnWidth,
        align: 'center',
        fixed: 'left',
        render: (address: string, record: any) => {
          // 合计行特殊处理
          if (record.id === 'summary') {
            return (
              <div style={{
                fontWeight: 'bold',
                fontSize: '14px',
                color: '#1890ff',
                padding: '8px 0'
              }}>
                📊 合计 ({wallets.length} 个钱包)
              </div>
            );
          }

          // 普通钱包行
          return (
            <div style={{ width: '100%', textAlign: 'center' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '6px',
                flexWrap: 'nowrap'
              }}>
                <Tooltip title={address}>
                  <Typography.Text
                    copyable={{ text: address, tooltips: false }}
                    style={{
                      maxWidth: `${addressColumnWidth - 35}px`, // 优化可用空间利用
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '12px',
                      fontFamily: 'monospace', // 使用等宽字体，更适合地址显示
                      transition: 'all 0.3s ease',
                      display: 'block',
                      whiteSpace: 'nowrap',
                      flexShrink: 1
                    }}
                  >
                    {isExpanded ? address : `${address.slice(0, 8)}...${address.slice(-6)}`}
                  </Typography.Text>
                </Tooltip>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteWallet(record.id)}
                  style={{
                    color: '#ff4d4f',
                    padding: '0 2px',
                    fontSize: '10px',
                    width: '20px',
                    height: '20px',
                    flexShrink: 0
                  }}
                />
              </div>
              {record.ensName && (
                <div style={{ 
                  fontSize: '10px', 
                  color: '#666', 
                  marginTop: '2px',
                  textAlign: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: `${addressColumnWidth - 20}px`
                }}>
                  {isExpanded ? record.ensName : (record.ensName.length > 15 ? `${record.ensName.slice(0, 15)}...` : record.ensName)}
                </div>
              )}
            </div>
          );
        }
      }
    ];

    // 为每个选中的链添加动态列
    Array.from(selectedChainIds).forEach(chainId => {
      const chainInfo = getChainInfo(chainId);
      if (!chainInfo) return;

      // 创建链的分组列
      const chainColumns = {
        title: (
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span style={{
              color: chainInfo.color,
              fontWeight: 'bold',
              fontSize: '12px'
            }}>
              {chainInfo.name}
            </span>
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                setCurrentChainId(chainId);
                setIsAddTokenVisible(true);
              }}
              style={{
                fontSize: '10px',
                padding: '0 4px',
                height: '20px',
                color: chainInfo.color,
                border: `1px solid ${chainInfo.color}`
              }}
              title={`为 ${chainInfo.name} 添加代币列`}
            />
          </div>
        ),
        children: [
          // 原生代币列
          {
            title: chainInfo.symbol,
            dataIndex: ['chainData', chainId, 'nativeBalance'],
            key: `${chainId}-balance`,
            width: balanceWidth,
            align: 'center',
            render: (balance: string, record: any) => {
              // 合计行特殊处理
              if (record.id === 'summary') {
                const balanceNum = parseFloat(balance || '0');
                return (
                  <div style={{
                    textAlign: 'center',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    color: '#1890ff',
                    backgroundColor: '#f0f8ff',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}>
                    {balanceNum.toFixed(4)}
                  </div>
                );
              }

              // 普通行处理
              const loading = isLoading[`${record.id}-${chainId}`];
              if (loading) {
                return <Spin size="small" />;
              }
              const balanceNum = parseFloat(balance || '0');
              return (
                <div style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  fontFamily: 'monospace'
                }}>
                  {balanceNum < 0.0001 && balanceNum > 0 ?
                    '<0.0001' :
                    balanceNum.toFixed(4)
                  }
                </div>
              );
            }
          },
          // 自定义代币列 - 基于 displayedTokens 状态
          ...(displayedTokens[chainId] || []).map((token, tokenIndex) => ({
            title: (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                <span>{token.symbol}</span>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveTokenColumn(chainId, tokenIndex)}
                  style={{
                    color: '#ff4d4f',
                    fontSize: '8px',
                    width: '14px',
                    height: '14px',
                    padding: 0
                  }}
                  title={`删除 ${token.symbol} 列`}
                />
              </div>
            ),
            key: `${chainId}-token-${tokenIndex}`,
            width: tokenWidth,
            align: 'center',
            render: (_: any, record: any) => {
              // 合计行特殊处理
              if (record.id === 'summary') {
                const tokenData = record.chainData[chainId]?.tokens?.find(
                  (t: TokenBalance) => t.contractAddress.toLowerCase() === token.contractAddress.toLowerCase()
                );
                if (!tokenData) {
                  return (
                    <div style={{ textAlign: 'center', color: '#ccc', fontSize: '10px' }}>
                      -
                    </div>
                  );
                }

                const balance = parseFloat(tokenData.balance || '0');
                return (
                  <div style={{
                    textAlign: 'center',
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    color: '#1890ff',
                    backgroundColor: '#f0f8ff',
                    padding: '4px',
                    borderRadius: '4px'
                  }}>
                    {balance.toFixed(2)}
                  </div>
                );
              }

              // 普通行处理
              const tokenData = record.chainData[chainId]?.tokens?.find(
                (t: TokenBalance) => t.contractAddress.toLowerCase() === token.contractAddress.toLowerCase()
              );
              
              if (!tokenData) {
                return (
                  <div style={{ textAlign: 'center', color: '#ccc', fontSize: '10px' }}>
                    -
                  </div>
                );
              }

              const balance = parseFloat(tokenData.balance || '0');
              return (
                <div style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  fontFamily: 'monospace'
                }}>
                  {balance < 0.01 && balance > 0 ? '<0.01' : balance.toFixed(2)}
                </div>
              );
            }
          })),
          // 交易数列
          {
            title: 'TX',
            dataIndex: ['chainData', chainId, 'txCount'],
            key: `${chainId}-tx`,
            width: txWidth,
            align: 'center',
            render: (txCount: number, record: any) => {
              // 合计行不显示TX数量
              if (record.id === 'summary') {
                return (
                  <div style={{
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#999',
                    fontWeight: 'bold'
                  }}>
                    -
                  </div>
                );
              }

              // 普通行处理
              const loading = isLoading[`${record.id}-${chainId}`];
              if (loading) {
                return <Spin size="small" />;
              }
              return (
                <div style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  fontFamily: 'monospace'
                }}>
                  {txCount || 0}
                </div>
              );
            }
          }
        ]
      };

      columns.push(chainColumns);
    });

    return columns;
  };

  // 删除钱包
  const handleDeleteWallet = (walletId: number) => {
    setWallets(prev => prev.filter(w => w.id !== walletId));
    setSelectedWalletIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(walletId);
      return newSet;
    });
    message.success('钱包已删除');
  };

  // 删除代币列
  const handleRemoveTokenColumn = (chainId: string, tokenIndex: number) => {
    // 从显示的代币列表中移除
    setDisplayedTokens(prev => {
      const newDisplayedTokens = { ...prev };
      if (newDisplayedTokens[chainId]) {
        newDisplayedTokens[chainId] = newDisplayedTokens[chainId].filter((_, index) => index !== tokenIndex);
        // 如果该网络没有代币了，删除空数组
        if (newDisplayedTokens[chainId].length === 0) {
          delete newDisplayedTokens[chainId];
        }
      }
      return newDisplayedTokens;
    });

    // 从所有钱包中移除对应的代币余额数据
    const removedToken = displayedTokens[chainId]?.[tokenIndex];
    if (removedToken) {
      setWallets(prev => prev.map(wallet => ({
        ...wallet,
        chainData: {
          ...wallet.chainData,
          [chainId]: {
            ...wallet.chainData[chainId],
            tokens: (wallet.chainData[chainId]?.tokens || []).filter(
              token => token.contractAddress.toLowerCase() !== removedToken.contractAddress.toLowerCase()
            )
          }
        }
      })));
    }

    message.success('代币列已删除');
  };



  // 计算合计数据
  const calculateTotalData = () => {
    const totals: Record<string, { nativeBalance: number; tokenBalances: Record<string, number> }> = {};

    // 初始化每个选中网络的合计数据
    Array.from(selectedChainIds).forEach(chainId => {
      totals[chainId] = {
        nativeBalance: 0,
        tokenBalances: {}
      };
    });

    // 遍历所有钱包计算合计
    wallets.forEach(wallet => {
      Array.from(selectedChainIds).forEach(chainId => {
        const chainData = wallet.chainData[chainId];
        if (chainData) {
          // 累加原生代币余额
          const nativeBalance = parseFloat(chainData.nativeBalance || '0');
          totals[chainId].nativeBalance += nativeBalance;

          // 累加代币余额 - 现在基于合约地址
          chainData.tokens?.forEach(token => {
            const balance = parseFloat(token.balance || '0');
            if (!totals[chainId].tokenBalances[token.contractAddress]) {
              totals[chainId].tokenBalances[token.contractAddress] = 0;
            }
            totals[chainId].tokenBalances[token.contractAddress] += balance;
          });
        }
      });
    });

    return totals;
  };

  // 生成合计行数据
  const generateSummaryRow = () => {
    if (!showRowTotal || wallets.length === 0) return null;

    const totals = calculateTotalData();

    return {
      id: 'summary',
      address: '合计',
      chainData: Object.fromEntries(
        Array.from(selectedChainIds).map(chainId => [
          chainId,
          {
            nativeBalance: totals[chainId]?.nativeBalance.toFixed(4) || '0',
            txCount: 0, // TX数量不合计
            tokens: (displayedTokens[chainId] || []).map(displayedToken => {
              const balance = totals[chainId]?.tokenBalances[displayedToken.contractAddress] || 0;
              return {
                contractAddress: displayedToken.contractAddress,
                symbol: displayedToken.symbol,
                decimals: displayedToken.decimals,
                balance: balance.toFixed(2)
              };
            })
          }
        ])
      )
    };
  };

  // 刷新钱包数据（带重试机制）- 修复重复请求问题
  const handleRefresh = useCallback(async (walletAddress: string, chainId: string, retryCount = 0) => {
    const chainInfo = getChainInfo(chainId);
    if (!chainInfo) return;

    const wallet = wallets.find(w => w.address === walletAddress);
    if (!wallet) return;

    const loadingKey = `${wallet.id}-${chainId}`;
    const refreshKey = `${walletAddress}-${chainId}`;
    
    // 双重检查锁机制，防止重复刷新
    if (refreshLocks.current[refreshKey]) {
      console.log(`刷新被跳过：${walletAddress} 在 ${chainInfo.name} 上正在进行中`);
      return;
    }
    
    // 检查loading状态
    if (isLoading[loadingKey]) {
      console.log(`刷新被跳过：${walletAddress} 在 ${chainInfo.name} 正在加载中`);
      return;
    }

    // 设置刷新锁和loading状态
    refreshLocks.current[refreshKey] = true;
    console.log(`开始刷新：${walletAddress} 在 ${chainInfo.name}`);
    setIsLoading(prev => ({ ...prev, [loadingKey]: true }));

    // 简化的RPC节点列表 - 只保留最稳定的节点
    const fallbackRpcs = [
      ...(chainId === '1' ? [
        'https://ethereum.publicnode.com',
        'https://rpc.flashbots.net'
      ] : []),
      ...(chainId === '56' ? [
        'https://bsc.publicnode.com',
        'https://bsc-dataseed1.binance.org'
      ] : []),
      ...(chainId === '137' ? [
        'https://polygon.publicnode.com',
        'https://polygon-rpc.com'
      ] : []),
      ...(chainId === '42161' ? [
        'https://arbitrum.publicnode.com'
      ] : []),
      ...(chainId === '10' ? [
        'https://optimism.publicnode.com'
      ] : []),
      ...(chainId === '8453' ? [
        'https://base.publicnode.com'
      ] : [])
    ];

    for (let i = 0; i < fallbackRpcs.length; i++) {
      try {
        console.log(`尝试连接 ${chainInfo.name} RPC[${i+1}/${fallbackRpcs.length}]: ${fallbackRpcs[i]}`);

        // 创建provider
        const provider = new ethers.JsonRpcProvider(fallbackRpcs[i], undefined, {
          staticNetwork: true
        });

        // 设置超时时间
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('请求超时')), 10000)
        );

        // 并发获取余额和交易数，带超时
        const [balance, txCount] = await Promise.race([
          Promise.all([
            provider.getBalance(walletAddress),
            provider.getTransactionCount(walletAddress)
          ]),
          timeout
        ]) as [bigint, number];

        // 转换余额为ETH单位
        const balanceInEth = ethers.formatEther(balance);

        // 获取该钱包在该链上的代币余额 - 基于 displayedTokens 配置
        const chainDisplayedTokens = displayedTokens[chainId] || [];
        const tokenBalances = await Promise.allSettled(
          chainDisplayedTokens.map(async (displayedToken) => {
            try {
              const contract = new ethers.Contract(
                displayedToken.contractAddress,
                ['function balanceOf(address) view returns (uint256)'],
                provider
              );

              const tokenTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('代币查询超时')), 5000)
              );

              const balance = await Promise.race([
                contract.balanceOf(walletAddress),
                tokenTimeout
              ]) as bigint;

              const balanceFormatted = ethers.formatUnits(balance, displayedToken.decimals);
              return {
                contractAddress: displayedToken.contractAddress,
                symbol: displayedToken.symbol,
                decimals: displayedToken.decimals,
                balance: balanceFormatted
              } as TokenBalance;
            } catch (error) {
              console.error(`获取代币 ${displayedToken.symbol} 余额失败:`, error);
              return {
                contractAddress: displayedToken.contractAddress,
                symbol: displayedToken.symbol,
                decimals: displayedToken.decimals,
                balance: '0'
              } as TokenBalance;
            }
          })
        );

        // 处理代币余额结果
        const processedTokenBalances = tokenBalances.map((result, index) =>
          result.status === 'fulfilled' ? result.value : {
            contractAddress: chainDisplayedTokens[index].contractAddress,
            symbol: chainDisplayedTokens[index].symbol,
            decimals: chainDisplayedTokens[index].decimals,
            balance: '0'
          } as TokenBalance
        );

        // 更新钱包数据
        setWallets(prev => {
          const updatedWallets = prev.map(w => {
            if (w.id === wallet.id) {
              return {
                ...w,
                chainData: {
                  ...w.chainData,
                  [chainId]: {
                    nativeBalance: balanceInEth,
                    txCount: txCount,
                    tokens: processedTokenBalances
                  }
                }
              };
            }
            return w;
          });

          // 立即保存更新后的钱包数据到localStorage
          try {
            localStorage.setItem('wallet_manager_wallets', safeStringify(updatedWallets));
            console.log(`${chainInfo.name} 刷新数据已保存到localStorage - ${walletAddress}`);
          } catch (error) {
            console.error('保存刷新数据失败:', error);
          }

          return updatedWallets;
        });

        console.log(`${chainInfo.name} 数据刷新成功 - ${walletAddress} (余额: ${balanceInEth}, 交易数: ${txCount})`);
        
        // 成功后释放锁和loading状态
        refreshLocks.current[refreshKey] = false;
        setIsLoading(prev => ({ ...prev, [loadingKey]: false }));
        return;

      } catch (error) {
        console.error(`RPC ${fallbackRpcs[i]} 连接失败:`, error);

        // 如果是最后一个RPC也失败了
        if (i === fallbackRpcs.length - 1) {
          console.error(`所有 ${chainInfo.name} RPC 节点都失败了 - ${walletAddress}`);
          message.error(`刷新 ${chainInfo.name} 数据失败`);
          
          // 失败后释放锁和loading状态
          refreshLocks.current[refreshKey] = false;
          setIsLoading(prev => ({ ...prev, [loadingKey]: false }));
          return;
        }

        // 继续尝试下一个RPC，简单延迟
        console.log(`RPC[${i+1}] 失败，尝试下一个节点...`);
        await new Promise(resolve => setTimeout(resolve, 300));
        continue;
      }
    }
    
    // 兜底逻辑：如果执行到这里说明有异常情况，确保清理状态
    console.warn(`异常退出刷新流程：${walletAddress} 在 ${chainInfo.name}`);
    refreshLocks.current[refreshKey] = false;
    setIsLoading(prev => ({ ...prev, [loadingKey]: false }));
  }, [wallets, selectedChainIds, displayedTokens, isLoading]);

  // 批量刷新所有数据
  const handleRefreshAll = async () => {
    if (wallets.length === 0) {
      message.warning('没有钱包地址需要刷新');
      return;
    }

    // 防止重复批量刷新
    if (batchRefreshLock.current) {
      console.log('批量刷新已在进行中，跳过重复请求');
      message.warning('批量刷新正在进行中，请勿重复点击');
      return;
    }

    // 设置批量刷新锁
    batchRefreshLock.current = true;
    console.log(`开始刷新 ${wallets.length} 个钱包的 ${selectedChainIds.size} 个网络数据...`);
    message.info('开始刷新所有钱包数据...');

    // 分批处理，避免同时发起过多请求
    const batchSize = 5; // 每批处理5个钱包
    let completedCount = 0;
    const totalCount = wallets.length * selectedChainIds.size;

    try {
      for (let i = 0; i < wallets.length; i += batchSize) {
        const walletBatch = wallets.slice(i, i + batchSize);
        const batchPromises: Promise<void>[] = [];

        walletBatch.forEach(wallet => {
          Array.from(selectedChainIds).forEach(chainId => {
            batchPromises.push(handleRefresh(wallet.address, chainId));
          });
        });

        await Promise.allSettled(batchPromises);
        completedCount += batchPromises.length;
        
        console.log(`已完成 ${completedCount}/${totalCount} 个刷新任务`);
        
        // 批次之间延迟，避免RPC限流
        if (i + batchSize < wallets.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // 批量刷新完成后，确保所有数据已保存
      try {
        localStorage.setItem('wallet_manager_wallets', safeStringify(wallets));
        localStorage.setItem('wallet_manager_selected_chains', safeStringify(Array.from(selectedChainIds)));
        localStorage.setItem('wallet_manager_displayed_tokens', safeStringify(displayedTokens));
        console.log('批量刷新完成，所有数据已保存到localStorage');
      } catch (error) {
        console.error('批量刷新后保存数据失败:', error);
      }
      
      message.success('所有数据刷新完成并已保存');
    } catch (error) {
      console.error('批量刷新失败:', error);
      message.error('批量刷新过程中出现错误');
    } finally {
      // 无论成功失败都要释放批量刷新锁
      batchRefreshLock.current = false;
      console.log('批量刷新锁已释放');
    }
  };

  // 刷新选中的钱包
  const handleRefreshSelected = async () => {
    if (selectedWalletIds.size === 0) {
      message.warning('请先选择要刷新的钱包');
      return;
    }

    // 防止重复刷新选中钱包
    if (selectedRefreshLock.current) {
      console.log('选中钱包刷新已在进行中，跳过重复请求');
      message.warning('选中钱包刷新正在进行中，请勿重复点击');
      return;
    }

    // 设置选中刷新锁
    selectedRefreshLock.current = true;
    const selectedWallets = wallets.filter(w => selectedWalletIds.has(w.id));
    console.log(`开始刷新 ${selectedWallets.length} 个选中钱包的 ${selectedChainIds.size} 个网络数据...`);
    message.info(`开始刷新 ${selectedWalletIds.size} 个钱包数据...`);

    // 分批处理选中的钱包
    const batchSize = 3; // 选中刷新时使用更小的批次
    let completedCount = 0;
    const totalCount = selectedWallets.length * selectedChainIds.size;

    try {
      for (let i = 0; i < selectedWallets.length; i += batchSize) {
        const walletBatch = selectedWallets.slice(i, i + batchSize);
        const batchPromises: Promise<void>[] = [];

        walletBatch.forEach(wallet => {
          Array.from(selectedChainIds).forEach(chainId => {
            batchPromises.push(handleRefresh(wallet.address, chainId));
          });
        });

        await Promise.allSettled(batchPromises);
        completedCount += batchPromises.length;
        
        console.log(`已完成 ${completedCount}/${totalCount} 个选中钱包刷新任务`);
        
        // 批次之间延迟
        if (i + batchSize < selectedWallets.length) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      // 选中钱包刷新完成后，确保所有数据已保存
      try {
        localStorage.setItem('wallet_manager_wallets', safeStringify(wallets));
        localStorage.setItem('wallet_manager_selected_chains', safeStringify(Array.from(selectedChainIds)));
        localStorage.setItem('wallet_manager_displayed_tokens', safeStringify(displayedTokens));
        console.log('选中钱包刷新完成，所有数据已保存到localStorage');
      } catch (error) {
        console.error('选中钱包刷新后保存数据失败:', error);
      }
      
      message.success('选中钱包数据刷新完成并已保存');
    } catch (error) {
      console.error('刷新选中钱包失败:', error);
      message.error('刷新过程中出现错误');
    } finally {
      // 无论成功失败都要释放选中刷新锁
      selectedRefreshLock.current = false;
      console.log('选中钱包刷新锁已释放');
    }
  };

  // 添加自定义代币
  const handleAddToken = async () => {
    if (!newTokenAddress) {
      message.error('请输入代币合约地址');
      return;
    }

    if (!isValidAddress(newTokenAddress)) {
      message.error('无效的合约地址');
      return;
    }

    if (!currentChainId) {
      message.error('请选择网络');
      return;
    }

    const chainInfo = getChainInfo(currentChainId);
    if (!chainInfo) return;

    // 检查是否已经添加过这个代币
    const existingTokens = displayedTokens[currentChainId] || [];
    const existingToken = existingTokens.find(
      token => token.contractAddress.toLowerCase() === newTokenAddress.toLowerCase()
    );

    if (existingToken) {
      message.warning('该代币已存在于此网络的显示列表中');
      return;
    }

    try {
      // 创建provider和合约实例
      const provider = new ethers.JsonRpcProvider(chainInfo.rpcUrl);
      const contract = new ethers.Contract(
        newTokenAddress,
        [
          'function symbol() view returns (string)',
          'function decimals() view returns (uint8)',
          'function balanceOf(address) view returns (uint256)'
        ],
        provider
      );

      // 获取代币信息
      const [symbol, decimals] = await Promise.all([
        contract.symbol(),
        contract.decimals()
      ]);

      console.log(`获取到代币信息: ${symbol}, decimals: ${decimals}`);

      // 添加到显示的代币列表中
      const newCustomToken: CustomToken = {
        contractAddress: newTokenAddress,
        symbol: symbol,
        decimals: decimals
      };

      // 计算更新后的 displayedTokens
      const updatedDisplayedTokens = {
        ...displayedTokens,
        [currentChainId]: [...(displayedTokens[currentChainId] || []), newCustomToken]
      };

      console.log('更新前的displayedTokens:', displayedTokens);
      console.log('更新后的displayedTokens:', updatedDisplayedTokens);

      setDisplayedTokens(updatedDisplayedTokens);

      // 添加限制：一次最多查询前100个钱包，避免过多并发请求
      const walletsToQuery = wallets.slice(0, 100);
      console.log(`为 ${walletsToQuery.length} 个钱包查询 ${symbol} 代币余额...`);
      
      const tokenBalancePromises = walletsToQuery.map(async (wallet) => {
        try {
          // 添加超时控制，避免长时间等待
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('查询超时')), 8000)
          );
          
          const balance = await Promise.race([
            contract.balanceOf(wallet.address),
            timeoutPromise
          ]) as bigint;
          
          const balanceFormatted = ethers.formatUnits(balance, decimals);

          const newToken: TokenBalance = {
            contractAddress: newTokenAddress,
            symbol: symbol,
            decimals: decimals,
            balance: balanceFormatted
          };

          return { walletId: wallet.id, token: newToken };
        } catch (error) {
          console.error(`获取钱包 ${wallet.address} 的 ${symbol} 余额失败:`, error);
          return {
            walletId: wallet.id,
            token: {
              contractAddress: newTokenAddress,
              symbol: symbol,
              decimals: decimals,
              balance: '0'
            }
          };
        }
      });

      // 分批处理，避免过多并发请求
      const batchSize = 10;
      const results: PromiseSettledResult<{ walletId: number; token: TokenBalance }>[] = [];
      
      for (let i = 0; i < tokenBalancePromises.length; i += batchSize) {
        const batch = tokenBalancePromises.slice(i, i + batchSize);
        const batchResults = await Promise.allSettled(batch);
        results.push(...batchResults);
        
        // 批次之间稍微延迟，避免RPC限流
        if (i + batchSize < tokenBalancePromises.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

            // 更新钱包数据
      const updatedWallets = wallets.map(wallet => {
        const result = results.find(r => 
          r.status === 'fulfilled' && r.value.walletId === wallet.id
        );
        
        if (result && result.status === 'fulfilled') {
          const existingTokens = wallet.chainData[currentChainId]?.tokens || [];
          // 检查是否已经存在这个代币
          const hasToken = existingTokens.some(
            t => t.contractAddress.toLowerCase() === newTokenAddress.toLowerCase()
          );
          
          if (!hasToken) {
            return {
              ...wallet,
              chainData: {
                ...wallet.chainData,
                [currentChainId]: {
                  ...wallet.chainData[currentChainId],
                  tokens: [...existingTokens, result.value.token]
                }
              }
            };
          }
        }
        return wallet;
      });

      // 更新状态
      setWallets(updatedWallets);

      // 使用setTimeout确保状态更新完成后再保存，避免异步问题
      setTimeout(() => {
        try {
          localStorage.setItem('wallet_manager_wallets', safeStringify(updatedWallets));
          localStorage.setItem('wallet_manager_displayed_tokens', safeStringify(updatedDisplayedTokens));
          localStorage.setItem('wallet_manager_selected_chains', safeStringify(Array.from(selectedChainIds)));
          console.log(`代币 ${symbol} 配置和余额数据已完整保存到localStorage`);
          console.log('保存的displayedTokens:', safeStringify(updatedDisplayedTokens));
          console.log('保存的wallets数量:', updatedWallets.length);
        } catch (error) {
          console.error('保存代币数据失败:', error);
        }
      }, 100);

      // 重置状态
      setNewTokenAddress('');
      setIsAddTokenVisible(false);
      setCurrentChainId(null);

      message.success(`成功添加代币 ${symbol} 列，已为所有钱包查询余额并保存`);

    } catch (error) {
      console.error('添加代币失败:', error);
      message.error('添加代币失败，请检查合约地址是否正确');
    }
  };

  // 批量导入钱包地址
  const handleBatchImport = () => {
    const addresses = batchAddresses
      .split('\n')
      .map(addr => addr.trim())
      .filter(addr => addr.length > 0);

    if (addresses.length === 0) {
      message.error('请输入至少一个钱包地址');
      return;
    }

    let successCount = 0;
    let duplicateCount = 0;
    let invalidCount = 0;

    addresses.forEach(address => {
      if (!isValidAddress(address)) {
        invalidCount++;
        return;
      }

      const existingWallet = wallets.find(w => w.address.toLowerCase() === address.toLowerCase());
      if (existingWallet) {
        duplicateCount++;
        return;
      }

      // 直接添加钱包，不调用handleAddWallet函数
      const newWallet: Wallet = {
        id: Date.now() + successCount, // 确保ID唯一
        address: address,
        chainData: {}
      };

      // 为每个支持的链初始化空数据
      SUPPORTED_CHAINS.forEach(chain => {
        newWallet.chainData[chain.id] = {
          nativeBalance: '0',
          txCount: 0,
          tokens: []
        };
      });

      setWallets(prev => [...prev, newWallet]);
      successCount++;
    });

    // 显示导入结果
    const resultMessages = [];
    if (successCount > 0) resultMessages.push(`成功导入 ${successCount} 个地址`);
    if (duplicateCount > 0) resultMessages.push(`跳过 ${duplicateCount} 个重复地址`);
    if (invalidCount > 0) resultMessages.push(`忽略 ${invalidCount} 个无效地址`);

    message.success(resultMessages.join('，'));

    // 重置状态
    setBatchAddresses('');
    setIsBatchImportVisible(false);
  };

  // JSON导入功能
  const handleJsonImport = () => {
    setIsJsonImportVisible(true);
  };

  // 处理文件上传和导入
  const handleFileImport = async (file: File) => {
    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      // 验证导入数据格式
      if (!importData.wallets || !Array.isArray(importData.wallets)) {
        message.error('无效的导入文件格式：缺少钱包数据');
        return false;
      }

      if (!importData.selectedChains || !Array.isArray(importData.selectedChains)) {
        message.error('无效的导入文件格式：缺少网络配置');
        return false;
      }

      // 导入钱包数据
      const importedWallets = importData.wallets.map((wallet: any, index: number) => ({
        id: Date.now() + index, // 生成新的ID避免冲突
        address: wallet.address,
        ensName: wallet.ensName,
        chainData: wallet.chainData || {}
      }));

            // 导入网络选择配置
      const importedChains = new Set<string>(importData.selectedChains);

      // 导入显示的代币配置（如果存在）
      const importedDisplayedTokens = importData.displayedTokens || {};

      // 合并数据
      const mergedWallets = [...wallets];
      let addedCount = 0;
      let duplicateCount = 0;

      importedWallets.forEach((importedWallet: Wallet) => {
        const existing = wallets.find(w => w.address.toLowerCase() === importedWallet.address.toLowerCase());
        if (!existing) {
          mergedWallets.push(importedWallet);
          addedCount++;
        } else {
          duplicateCount++;
          // 合并链数据，保留现有数据但补充缺失的链数据
          Object.keys(importedWallet.chainData).forEach(chainId => {
            if (!existing.chainData[chainId]) {
              existing.chainData[chainId] = importedWallet.chainData[chainId];
            }
          });
        }
      });

      // 合并网络选择
      const mergedChains = new Set<string>([...Array.from(selectedChainIds), ...Array.from(importedChains)]);

      // 合并显示的代币配置
      const mergedDisplayedTokens = { ...displayedTokens };
      Object.keys(importedDisplayedTokens).forEach(chainId => {
        if (!mergedDisplayedTokens[chainId]) {
          mergedDisplayedTokens[chainId] = importedDisplayedTokens[chainId];
        } else {
          // 合并代币列表，避免重复
          const existingTokens = mergedDisplayedTokens[chainId];
          const importedTokens = importedDisplayedTokens[chainId];
          
          importedTokens.forEach((token: CustomToken) => {
            const exists = existingTokens.some(existing => 
              existing.contractAddress.toLowerCase() === token.contractAddress.toLowerCase()
            );
            if (!exists) {
              existingTokens.push(token);
            }
          });
        }
      });

      // 更新状态
      setWallets(mergedWallets);
      setSelectedChainIds(mergedChains);
      setDisplayedTokens(mergedDisplayedTokens);

      // 立即保存到localStorage
      try {
        localStorage.setItem('wallet_manager_wallets', safeStringify(mergedWallets));
        localStorage.setItem('wallet_manager_selected_chains', safeStringify(Array.from(mergedChains)));
        localStorage.setItem('wallet_manager_displayed_tokens', safeStringify(mergedDisplayedTokens));
      } catch (error) {
        console.error('保存导入数据失败:', error);
      }

      // 显示导入结果
      const resultMessages = [];
      if (addedCount > 0) resultMessages.push(`新增 ${addedCount} 个钱包`);
      if (duplicateCount > 0) resultMessages.push(`跳过 ${duplicateCount} 个重复钱包`);
      resultMessages.push(`网络配置已合并`);
      if (Object.keys(importedDisplayedTokens).length > 0) {
        resultMessages.push(`代币配置已合并`);
      }

      message.success(`导入完成：${resultMessages.join('，')}`);
      setIsJsonImportVisible(false);
      setImportFileList([]);
      
      return false; // 阻止自动上传
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败：文件格式错误或数据无效');
      return false;
    }
  };

  // 导出功能选择
  const handleExportSelected = () => {
    if (selectedWalletIds.size === 0) {
      message.warning('请先选择要导出的钱包');
      return;
    }
    setIsExportModalVisible(true);
  };

  // 执行导出
  const executeExport = () => {
    if (selectedWalletIds.size === 0) {
      message.warning('请先选择要导出的钱包');
      return;
    }

    const selectedWallets = wallets.filter(w => selectedWalletIds.has(w.id));
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '');

    if (exportFormat === 'json') {
      // JSON导出 - 包含完整配置信息
      const exportData = {
        exportTime: new Date().toISOString(),
        selectedChains: Array.from(selectedChainIds),
        displayedTokens: displayedTokens,
        wallets: selectedWallets.map(wallet => ({
          address: wallet.address,
          ensName: wallet.ensName,
          chainData: wallet.chainData
        }))
      };

      const dataStr = safeStringify(exportData);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      saveAs(dataBlob, `wallet-config-${today}.json`);
      
      message.success(`已导出 ${selectedWallets.length} 个钱包的完整配置数据`);
    } else {
      // Excel导出 - 只包含地址、余额和TX信息
      const excelData: any[] = [];

      // 准备表头
      const headers = ['地址', 'ENS名称'];
      Array.from(selectedChainIds).forEach(chainId => {
        const chainInfo = getChainInfo(chainId);
        if (chainInfo) {
          headers.push(`${chainInfo.name} (${chainInfo.symbol})`);
          
          // 添加自定义代币列
          const chainTokens = displayedTokens[chainId] || [];
          chainTokens.forEach(token => {
            headers.push(`${chainInfo.name} (${token.symbol})`);
          });
          
          headers.push(`${chainInfo.name} TX数量`);
        }
      });
      
      excelData.push(headers);

      // 准备数据行
      selectedWallets.forEach(wallet => {
        const row: any[] = [wallet.address, wallet.ensName || ''];
        
        Array.from(selectedChainIds).forEach(chainId => {
          const chainData = wallet.chainData[chainId];
          
          if (chainData) {
            // 原生代币余额
            const balance = parseFloat(chainData.nativeBalance || '0');
            row.push(balance.toFixed(4));
            
            // 自定义代币余额
            const chainTokens = displayedTokens[chainId] || [];
            chainTokens.forEach(token => {
              const tokenData = chainData.tokens?.find(
                t => t.contractAddress.toLowerCase() === token.contractAddress.toLowerCase()
              );
              const tokenBalance = tokenData ? parseFloat(tokenData.balance || '0') : 0;
              row.push(tokenBalance.toFixed(2));
            });
            
            // TX数量
            row.push(chainData.txCount || 0);
          } else {
            // 没有数据的情况，填充空值
            const chainTokens = displayedTokens[chainId] || [];
            const emptyCols = 2 + chainTokens.length; // 原生代币 + 自定义代币 + TX
            for (let i = 0; i < emptyCols; i++) {
              row.push(0);
            }
          }
        });
        
        excelData.push(row);
      });

      // 创建工作表
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      
      // 设置列宽
      const colWidths = [
        { wch: 45 }, // 地址列
        { wch: 20 }, // ENS名称列
      ];
      
      // 为每个网络的列设置宽度
      Array.from(selectedChainIds).forEach(chainId => {
        const chainTokens = displayedTokens[chainId] || [];
        colWidths.push({ wch: 15 }); // 原生代币列
        chainTokens.forEach(() => {
          colWidths.push({ wch: 15 }); // 自定义代币列
        });
        colWidths.push({ wch: 10 }); // TX列
      });
      
      ws['!cols'] = colWidths;

      // 创建工作簿
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '钱包资产信息');

      // 导出文件
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(excelBlob, `walletInfoEVM_${today}.xlsx`);
      
      message.success(`已导出 ${selectedWallets.length} 个钱包的Excel数据`);
    }

    setIsExportModalVisible(false);
  };

  // 删除选中的钱包
  const handleDeleteSelected = () => {
    if (selectedWalletIds.size === 0) {
      message.warning('请先选择要删除的钱包');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedWalletIds.size} 个钱包吗？此操作不可恢复。`,
      okText: '确认删除',
      cancelText: '取消',
      okType: 'danger',
      onOk: () => {
        setWallets(prev => prev.filter(w => !selectedWalletIds.has(w.id)));
        setSelectedWalletIds(new Set());
        message.success(`已删除 ${selectedWalletIds.size} 个钱包`);
      }
    });
  };

  // 组件初始化
  useEffect(() => {
    loadDataFromStorage();
  }, [loadDataFromStorage]);

  // 数据变化时保存 - 使用独立的保存函数避免重复调用
  useEffect(() => {
    // 只有在有实际数据时才保存，避免空数据触发保存
    if (wallets.length > 0 || selectedChainIds.size > 0 || Object.keys(displayedTokens).length > 0) {
      console.log('数据变化，准备保存...', { 
        walletsCount: wallets.length, 
        chainsCount: selectedChainIds.size, 
        tokensCount: Object.keys(displayedTokens).length 
      });
      // 使用简单的超时延迟保存，避免防抖函数重建问题
      const saveTimer = setTimeout(() => {
        saveDataToStorage();
      }, 1000);
      
      return () => clearTimeout(saveTimer);
    }
  }, [wallets, selectedChainIds, displayedTokens, saveDataToStorage]);

  return (
    <div style={{
      maxWidth: window.innerWidth <= 768 ? '100vw' : '96vw', // 移动端使用全宽，桌面端留小边距
      margin: '0 auto',
      padding: window.innerWidth <= 768 ? '0 var(--spacing-sm)' : '0 var(--spacing-md)',
      minWidth: window.innerWidth <= 768 ? 'auto' : '1200px' // 移动端不设最小宽度
    }}>

      {/* 顶部网络选择区 */}
      <Card className="spacing-responsive">
        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <Typography.Text strong style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>
            选择要显示的网络
          </Typography.Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {SUPPORTED_CHAINS.map(chain => (
              <Tag.CheckableTag
                key={chain.id}
                checked={selectedChainIds.has(chain.id)}
                onChange={(checked) => {
                  const newSelectedChains = new Set(selectedChainIds);
                  if (checked) {
                    newSelectedChains.add(chain.id);
                  } else {
                    newSelectedChains.delete(chain.id);
                  }
                  setSelectedChainIds(newSelectedChains);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: `2px solid ${chain.color}`,
                  backgroundColor: selectedChainIds.has(chain.id) ? chain.color : 'transparent',
                  color: selectedChainIds.has(chain.id) ? 'white' : chain.color,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <span style={{ marginRight: '6px' }}>●</span>
                {chain.name} ({chain.symbol})
              </Tag.CheckableTag>
            ))}
          </div>
        </div>
      </Card>

      {/* 操作工具栏 */}
      <Card className="spacing-responsive">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Space wrap>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setIsBatchImportVisible(true)}
                style={primaryButtonStyle}
              >
                批量导入地址
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setIsAddWalletVisible(true)}
                style={buttonStyle}
              >
                添加地址
              </Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRefreshAll}
                loading={Object.values(isLoading).some(loading => loading)}
                style={primaryButtonStyle}
              >
                刷新全部
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefreshSelected}
                disabled={selectedWalletIds.size === 0}
                loading={Object.values(isLoading).some(loading => loading)}
                style={buttonStyle}
              >
                刷新选中
              </Button>
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8} style={{ textAlign: 'center' }}>
            <Space>
              <Checkbox
                checked={showRowTotal}
                onChange={(e) => setShowRowTotal(e.target.checked)}
              >
                显示行合计
              </Checkbox>
              <Button
                icon={isExpanded ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setIsExpanded(!isExpanded)}
                style={buttonStyle}
              >
                {isExpanded ? '隐藏地址' : '显示完整地址'}
              </Button>
            </Space>
          </Col>

          <Col xs={24} sm={24} md={8} style={{ textAlign: 'right' }}>
            <Space>
              <span style={{ fontSize: '13px', color: '#666' }}>
                钱包总数: <strong>{wallets.length}</strong> |
                已选择: <strong>{selectedWalletIds.size}</strong>
              </span>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 钱包资产表格 */}
      <Card className="spacing-responsive" style={{ overflow: 'hidden' }}>
        <style>
          {`
            .summary-row {
              background-color: #f8f9fa !important;
              border-top: 2px solid #1890ff !important;
              font-weight: bold;
            }
            .summary-row:hover {
              background-color: #f8f9fa !important;
            }
            .summary-row .ant-table-cell {
              border-bottom: 2px solid #1890ff !important;
            }
          `}
        </style>
        <div style={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden'
        }}>
          <Table
            dataSource={showRowTotal ? [...wallets, generateSummaryRow()].filter(Boolean) : wallets}
            columns={generateTableColumns()}
            rowKey="id"
            scroll={{
              x: (() => {
                // 重新计算宽度（因为变量作用域问题）
                const selectedNetworkCount = selectedChainIds.size;
                let addressWidth;
                if (selectedNetworkCount <= 2) {
                  addressWidth = isExpanded ? 220 : 140;
                } else if (selectedNetworkCount <= 4) {
                  addressWidth = isExpanded ? 180 : 120;
                } else {
                  addressWidth = isExpanded ? 160 : 100;
                }
                
                const fixedWidth = 50 + addressWidth;
                let estimatedNetworkWidth;
                
                if (selectedNetworkCount <= 2) {
                  estimatedNetworkWidth = 190; // 每个网络大约需要的宽度
                } else if (selectedNetworkCount <= 4) {
                  estimatedNetworkWidth = 165;
                } else {
                  estimatedNetworkWidth = 135;
                }
                
                const totalNetworkWidth = selectedNetworkCount * estimatedNetworkWidth;
                const totalNeededWidth = fixedWidth + totalNetworkWidth;
                
                // 只有当需要的宽度超过可用宽度时才启用水平滚动
                return totalNeededWidth > availableWidth ? totalNeededWidth : undefined;
              })(),
              y: 600 // 设置垂直滚动
            }}
            pagination={false}
            size="small"
            rowSelection={{
              selectedRowKeys: Array.from(selectedWalletIds),
              onChange: (selectedRowKeys) => {
                setSelectedWalletIds(new Set(selectedRowKeys as number[]));
              },
              columnWidth: 40,
              getCheckboxProps: (record) => ({
                disabled: record?.id === 'summary', // 合计行不可选择
              }),
            }}
            loading={Object.values(isLoading).some(loading => loading)}
            style={{
              minWidth: '100%',
              fontSize: '12px' // 使用更小的字体以增加信息密度
            }}
            tableLayout="fixed"
            rowClassName={(record) => record?.id === 'summary' ? 'summary-row' : ''}
          />
        </div>

        {wallets.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#999'
          }}>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>📝</div>
            <div>暂无钱包地址</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              点击"添加地址"或"批量导入地址"开始管理您的钱包
            </div>
          </div>
        )}
      </Card>

      {/* 底部操作栏 */}
      <Card className="spacing-responsive">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Space>
              
              <Button
                icon={<ImportOutlined />}
                onClick={handleJsonImport}
                style={buttonStyle}
              >
                导入配置
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportSelected}
                disabled={selectedWalletIds.size === 0}
                style={buttonStyle}
              >
                导出选中
              </Button>
              <Button
                icon={<DeleteOutlined />}
                onClick={handleDeleteSelected}
                disabled={selectedWalletIds.size === 0}
                danger
                style={buttonStyle}
              >
                删除选中
              </Button>
            </Space>
          </Col>

          <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  // 重置所有刷新状态和锁
                  refreshLocks.current = {};
                  batchRefreshLock.current = false;
                  selectedRefreshLock.current = false;
                  setIsLoading({});
                  console.log('已重置所有刷新状态和锁');
                  message.info('刷新状态已重置');
                }}
                style={{ fontSize: '12px', color: '#ff4d4f' }}
              >
                重置刷新状态
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 添加钱包地址 Modal */}
      <Modal
        title="添加钱包地址"
        open={isAddWalletVisible}
        onOk={handleAddWallet}
        onCancel={() => {
          setIsAddWalletVisible(false);
          setNewWalletAddress('');
        }}
        okText="添加"
        cancelText="取消"
        width={500}
        okButtonProps={{ style: primaryButtonStyle }}
      >
        <div style={{ marginBottom: '12px' }}>
          <Typography.Text type="secondary">
            请输入有效的以太坊钱包地址（支持所有EVM兼容链）
          </Typography.Text>
        </div>
        <Input
          value={newWalletAddress}
          onChange={(e) => setNewWalletAddress(e.target.value)}
          placeholder="请输入钱包地址，例如：0x..."
          style={componentStyle}
          onPressEnter={handleAddWallet}
        />
      </Modal>

      {/* 批量导入Modal */}
      <Modal
        title="批量导入钱包地址"
        open={isBatchImportVisible}
        onOk={handleBatchImport}
        onCancel={() => {
          setIsBatchImportVisible(false);
          setBatchAddresses('');
        }}
        okText="导入"
        cancelText="取消"
        okButtonProps={{
          style: primaryButtonStyle,
          disabled: !batchAddresses.trim()
        }}
      >
        <div style={{ margin: '20px 0' }}>
          <Input.TextArea
            placeholder="请输入钱包地址，每行一个&#10;例如：&#10;0x1234567890123456789012345678901234567890&#10;0x0987654321098765432109876543210987654321"
            value={batchAddresses}
            onChange={(e) => setBatchAddresses(e.target.value)}
            rows={6}
            style={componentStyle}
          />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
            支持以太坊格式的地址，每行一个，自动过滤无效和重复地址
          </div>
        </div>
      </Modal>

      {/* 添加代币Modal */}
      <Modal
        title={currentChainId ? `为 ${getChainInfo(currentChainId)?.name} 添加代币列` : "添加自定义代币"}
        open={isAddTokenVisible}
        onOk={handleAddToken}
        onCancel={() => {
          setIsAddTokenVisible(false);
          setNewTokenAddress('');
          setCurrentChainId(null);
        }}
        okText="添加"
        cancelText="取消"
        width={500}
        okButtonProps={{
          style: primaryButtonStyle,
          disabled: !newTokenAddress.trim() || !currentChainId
        }}
      >
        <div style={{ margin: '20px 0' }}>
          {/* 显示选中的网络 */}
          {currentChainId && (
            <div style={{ marginBottom: '16px' }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: '8px' }}>
                目标网络：
              </Typography.Text>
              <Tag
                style={{
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '14px',
                  border: `2px solid ${getChainInfo(currentChainId)?.color}`,
                  backgroundColor: getChainInfo(currentChainId)?.color,
                  color: 'white',
                }}
              >
                {getChainInfo(currentChainId)?.name}
              </Tag>
            </div>
          )}

          {/* 代币地址输入 */}
          <div style={{ marginBottom: '16px' }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: '8px' }}>
              代币合约地址：
            </Typography.Text>
            <Input
              placeholder="请输入ERC20代币合约地址"
              value={newTokenAddress}
              onChange={(e) => setNewTokenAddress(e.target.value)}
              onPressEnter={handleAddToken}
              style={componentStyle}
            />
          </div>

          {/* 说明信息 */}
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px',
            fontSize: '12px',
            color: '#52c41a'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              💡 操作说明：
            </div>
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              <li>将为表格添加一个新的代币列</li>
              <li>自动获取代币符号、精度信息</li>
              <li>查询所有钱包在该代币的余额</li>
              <li>新列将显示在该网络的原生代币和交易数之间</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* JSON导入Modal */}
      <Modal
        title="导入钱包配置"
        open={isJsonImportVisible}
        onOk={() => {
          if (importFileList.length === 0) {
            message.error('请选择要导入的配置文件');
            return;
          }
          // 文件处理在beforeUpload中完成
        }}
        onCancel={() => {
          setIsJsonImportVisible(false);
          setImportFileList([]);
        }}
        okText="导入"
        cancelText="取消"
        width={600}
        okButtonProps={{
          style: primaryButtonStyle,
          disabled: importFileList.length === 0
        }}
      >
        <div style={{ margin: '20px 0' }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: '12px' }}>
            选择配置文件：
          </Typography.Text>
          
          <Upload
            accept=".json"
            fileList={importFileList}
            beforeUpload={handleFileImport}
            onChange={({ fileList }) => setImportFileList(fileList)}
            onRemove={() => setImportFileList([])}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />} style={buttonStyle}>
              选择JSON配置文件
            </Button>
          </Upload>

          <div style={{ 
            marginTop: '16px',
            padding: '12px', 
            backgroundColor: '#fff7e6', 
            border: '1px solid #ffd591', 
            borderRadius: '6px',
            fontSize: '12px',
            color: '#d46b08'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              📄 导入说明：
            </div>
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              <li>导入之前通过"导出选中"功能导出的JSON配置文件</li>
              <li>支持导入钱包地址、网络选择配置、自定义代币列配置</li>
              <li>重复的钱包地址会被跳过，不会覆盖现有数据</li>
              <li>网络配置和代币配置会智能合并到现有配置中</li>
              <li>导入后所有配置会自动保存到本地存储</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* 导出格式选择Modal */}
      <Modal
        title="选择导出格式"
        open={isExportModalVisible}
        onOk={executeExport}
        onCancel={() => setIsExportModalVisible(false)}
        okText="导出"
        cancelText="取消"
        width={500}
        okButtonProps={{
          style: primaryButtonStyle
        }}
      >
        <div style={{ margin: '20px 0' }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: '12px' }}>
            选择导出格式：
          </Typography.Text>
          
          <Select
            value={exportFormat}
            onChange={setExportFormat}
            style={{ width: '100%', marginBottom: '16px' }}
            size="large"
          >
            <Select.Option value="json">
              <Space>
                <DownloadOutlined />
                JSON格式 - 完整配置（包含钱包、网络、代币配置）
              </Space>
            </Select.Option>
            <Select.Option value="excel">
              <Space>
                <FileExcelOutlined />
                Excel格式 - 余额表格（仅地址、余额、TX数据）
              </Space>
            </Select.Option>
          </Select>

          <div style={{ 
            padding: '12px', 
            backgroundColor: exportFormat === 'json' ? '#f6ffed' : '#fff7e6', 
            border: `1px solid ${exportFormat === 'json' ? '#b7eb8f' : '#ffd591'}`, 
            borderRadius: '6px',
            fontSize: '12px',
            color: exportFormat === 'json' ? '#52c41a' : '#d46b08'
          }}>
            {exportFormat === 'json' ? (
              <>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  📄 JSON格式说明：
                </div>
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  <li>包含完整的钱包配置信息</li>
                  <li>包含网络选择配置和自定义代币列配置</li>
                  <li>可用于在其他设备中导入相同配置</li>
                </ul>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  📊 Excel格式说明：
                </div>
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  <li>仅包含钱包地址、余额和交易数据</li>
                  <li>适合用于数据分析和报表</li>
                  <li>包含所有选中网络的原生代币和自定义代币余额</li>
                </ul>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* 使用提示 */}
      <Card size="small" style={{
        background: '#f6ffed',
        border: '1px solid #b7eb8f',
        marginTop: '20px'
      }}>
        <div style={{ color: '#52c41a' }}>
          <strong>💡 使用提示：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>支持管理多个钱包地址，查看在不同EVM网络上的资产情况</li>
            <li>已优化RPC连接策略，优先使用免费公共节点，自动选择最佳节点进行查询</li>
            <li>可以通过顶部标签选择要显示的网络，表格列会动态调整</li>
            <li><strong>动态代币列管理</strong>：点击表格中网络列头的"+"按钮，可为该网络添加自定义ERC20代币列</li>
            <li>添加代币后，表格会在该网络下自动显示新的代币列，并查询所有钱包的余额</li>
            <li>代币列标题旁有删除按钮，可随时移除不需要的代币列</li>
            <li><strong>列显示顺序</strong>：每个网络下按顺序显示：原生代币 → 自定义代币 → 交易数</li>
            <li><strong>合计行功能</strong>：勾选"显示行合计"后，表格底部会显示所有钱包的余额总和</li>
            <li><strong>智能容错机制</strong>：系统会自动尝试多个RPC节点，确保查询的稳定性和成功率</li>
            <li><strong>防重复机制</strong>：系统会自动防止重复的刷新请求，避免网络拥堵</li>
            <li>支持批量导入钱包地址，每行一个地址格式</li>
            <li>所有数据（钱包、网络选择、代币列配置）自动保存到浏览器本地</li>
            <li><strong>配置导入导出</strong>：支持导出完整配置到JSON文件，并可在其他设备导入相同配置</li>
            <li><strong>Excel数据导出</strong>：支持导出为Excel格式，包含地址、余额和TX数据，适合数据分析</li>
            <li><strong>智能数据合并</strong>：导入配置时会智能合并钱包、网络选择和代币配置，避免重复</li>
            <li><strong>状态重置</strong>：如遇功能异常，可使用"重置刷新状态"按钮清理锁定状态</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default WalletManager;
